import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, ContentService, environment, fmicTaxonomyToGearCategory, ProductService, UserService, WindowService, encodeFilenameForHeader } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AppService, AvoService } from "@mypxplat/xplat/web/core";
import { filter, take, takeUntil } from "rxjs/operators";
import { FileUploader } from "ng2-file-upload";
import { Router } from "@angular/router";
import { RegisterProductComponent } from "../register-product/register-product.component";
import { TranslateService } from "@ngx-translate/core";

/** One row of `GET /product/fender-gear/search`, as rendered in the product picker. */
interface FenderProductSearchResult {
  sku: string;
  title: string;
  imageUrl: string;
  productNo: string;
  color: string;
  series: string;
  bodyType: string;
  taxonomy: string;
  requiresPhotos: boolean;
  variantCount: number;
  /** Colour / series / body type, for the list row's secondary line. */
  subData: string[];
  /** Same values joined, for the single-line confirmation block. */
  subtitle: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-missing-fender-serial",
  templateUrl: "missing-fender-serial.component.html",
  standalone: false,
})
export class MissingFenderSerialComponent extends BaseComponent implements OnInit {
  @Output() inelligibleProductEntered: EventEmitter<any> = new EventEmitter();
  @Input() serial: string = "";
  public sku: string = "";
  public productName: string = "";
  /** Free-text product-name query typed into the Fender product search. */
  public productQuery: string = "";
  public searchingProducts: boolean = false;
  public productResults: Array<FenderProductSearchResult> = [];
  public productsSearched: boolean = false;
  public productsNextToken: string | null = null;
  public loadingMoreProducts: boolean = false;
  /** The product the user picked out of the search results, if any. */
  public selectedProduct: FenderProductSearchResult;
  // Photos are always required on the manual "can't find my product" path, and additionally on
  // products whose family is flagged requiresPhotos by paeapi (Tone Master Pro today) — those
  // grant free software, so support wants to eyeball the gear before converting the serial.
  public photosRequired: boolean = true;
  public shopLink: string = "";
  public serialImageUploader: FileUploader;
  public bodyImageUploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadingFile: any;
  public fileResponse: any;
  public hoveringFileDrops: any = {
    serial: false,
    body: false,
  };
  /** True once a search came back with nothing, so we can offer the manual form. */
  public skuNotFound: boolean = false;
  /** True once the customer has opted into describing their gear by hand. */
  public unknownSku: boolean = false;
  public fileUploadProgress: any = {
    serialImage: null,
    bodyImage: null,
  };
  /**
   * Whether a photo is being transcoded to JPEG right now. Decoding a full-size HEIC
   * takes several seconds, and the file input is already gone from the DOM by then
   * (the uploader queue is non-empty), so without this the modal looks frozen.
   */
  public convertingPhoto = {
    serial: false,
    body: false,
  };
  /**
   * Filename of a photo that is attached but not previewable, because no decoder in
   * this browser could read it. Stands in for the thumbnail so the customer can still
   * see that their photo went through.
   */
  public attachedPhotoName: { serial?: string; body?: string } = {};
  /**
   * In-flight conversions, so submit() can wait for them instead of tripping the
   * missing-photos validation on a photo the customer has already chosen.
   */
  private pendingPhotoConversions = new Set<Promise<void>>();
  public submitArgs: any = {};
  public saving: boolean = false;
  public savingComplete: boolean = false;
  public category: string = "electric_guitars";
  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public appService: AppService,
    public productService: ProductService,
    public contentService: ContentService,
    private _router: Router,
    private _win: WindowService,
    private avoService: AvoService,
    private translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.setupSerialImageUploader();
    this.setupBodyImageUploader();
  }

  /**
   * Fire Registration Started when user clicks "I don't know the SKU".
   * Only runs when userService.appId === 'fender'.
   */
  onUnknownSkuClick() {
    this.unknownSku = true;
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (!te || typeof te.registrationStarted !== "function") return;
          const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
          // Map category to AVO format: electric_guitars -> instrument, etc.
          const categoryMap: Record<string, string> = {
            electric_guitars: "instrument",
            acoustic_guitars: "instrument",
            bass_guitars: "instrument",
            amplifiers: "recording hardware",
            other: "instrument", // Default to instrument for "other"
          };
          const avoCategory = categoryMap[this.category] || "instrument";
          const extra = {
            userId_: this.user?.["fc-id"] ?? undefined,
            prsId: this.user?.id ?? undefined,
            fcId: this.user?.["fc-id"] ?? undefined,
            category: avoCategory,
            action: "I don't know the SKU",
          };
          const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
          te.registrationStarted(payload);
        });
    }
  }

  /**
   * Fire Registration Completed after successful fallback registration.
   * Only runs when userService.appId === 'fender'.
   */
  private fireRegistrationCompleted() {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof te.registrationCompleted !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
        const categoryMap: Record<string, string> = {
          electric_guitars: "instrument",
          acoustic_guitars: "instrument",
          bass_guitars: "instrument",
          amplifiers: "recording hardware",
          other: "instrument",
        };
        const avoCategory = categoryMap[this.category] || "instrument";
        const extra = {
          userId_: this.user?.["fc-id"] ?? undefined,
          prsId: this.user?.id ?? undefined,
          fcId: this.user?.["fc-id"] ?? undefined,
          category: avoCategory,
          productName: this.productName || this.registeredMissingSerial?.productName || "Fender Product",
          productType: avoCategory,
          sku: this.registeredMissingSerial?.sku || this.sku || undefined,
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.registrationCompleted(payload);
      });
  }

  public selectedSerialImageFile: any;
  public displaySerialImage: any;
  setupSerialImageUploader() {
    this.serialImageUploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    this.serialImageUploader.onProgressAll = (progress: any) => {
      if (this.contentService.uploadSession.filesMap[this.selectedSerialImageFile.file.name]) {
        this.contentService.uploadSession.filesMap[this.selectedSerialImageFile.file.name].progress = progress;
      }
      this.fileUploadProgress.serialImage = progress < 100 ? progress : null;
    };

    this.serialImageUploader.onErrorItem = (error: any) => {
      alert("There was an error uploading the serial image, please try again.");
      this.serialImageUploader.clearQueue();
      this.fileUploadProgress.serialImage = null;
      this.selectedSerialImageFile = undefined;
      this.displaySerialImage = undefined;
      this.hoveringFileDrops.serial = false;
      delete this.contentService.uploadSession.filesMap[error.file.name];
    };

    this.serialImageUploader.onAfterAddingFile = (file) => {
      this.handleAddedImage(file, "serial");
    };
  }

  /**
   * Shared post-add handling for both photo uploaders.
   *
   * iPhones hand over HEIC/HEIF, which no browser but Safari can decode — those
   * files used to upload fine and then render as a broken image in myadmin,
   * leaving support unable to verify the serial. Anything that isn't already a
   * web-safe format is transcoded to JPEG here, before it ever reaches S3.
   */
  private handleAddedImage(file: any, which: "serial" | "body") {
    const uploader = which === "serial" ? this.serialImageUploader : this.bodyImageUploader;
    file.withCredentials = false;

    const type = (file.file?.type || "").toLowerCase();
    const name = (file.file?.name || "").toLowerCase();
    const isWebSafe = type === "image/jpeg" || type === "image/png" || type === "image/gif";
    // Windows Chrome reports an empty type for HEIC, so fall back to the extension.
    const isHeic = type === "image/heic" || type === "image/heif" || /\.(heic|heif)$/.test(name);

    if (!isWebSafe && !isHeic && !type.startsWith("image/")) {
      alert("Please select an image file");
      uploader.clearQueue();
      return;
    }

    if (isWebSafe) {
      this.acceptImage(file, which);
      return;
    }

    this.convertingPhoto[which] = true;
    // The task removes itself from the pending set *before* it resolves. Deleting in a
    // separately-chained .then() would be a microtask too late: submit()'s Promise.all
    // could observe the set still populated and re-queue itself forever.
    const task: Promise<void> = this.convertToJpeg(file._file)
      .then((converted) => {
        file._file = converted;
        file.file.name = converted.name;
        file.file.type = converted.type;
        file.file.size = converted.size;
        this.acceptImage(file, which);
      })
      .catch((error) => {
        // Decoders keep lagging behind what phones emit — 10-bit and tiled HEIC from
        // recent iPhones defeat older builds of libheif. Refusing the photo would leave
        // support with nothing to verify, so the original is uploaded untouched instead:
        // myadmin falls back to a download link, and the server-side backfill converts
        // it later. The customer did nothing wrong, so they see no error.
        console.warn("Could not convert photo to JPEG, uploading the original instead.", error);
        this.acceptImage(file, which, { convertible: false });
      })
      .then(() => {
        this.convertingPhoto[which] = false;
        this.pendingPhotoConversions.delete(task);
      });

    this.pendingPhotoConversions.add(task);
  }

  /** Lowercased extension without the dot, for paeapi's content-type resolver. */
  private fileExtension(fileName: string): string {
    const match = /\.([^.]+)$/.exec(fileName || "");
    return match ? match[1].toLowerCase() : "";
  }

  /**
   * Queue the photo for upload and show the local preview.
   *
   * `convertible: false` means the bytes are still in a format this browser can't
   * decode, so a data URL would render as a broken image. The filename is shown
   * instead — the photo is attached and will upload either way.
   */
  private acceptImage(file: any, which: "serial" | "body", opts: { convertible?: boolean } = {}) {
    if (which === "serial") {
      this.selectedSerialImageFile = file;
    } else {
      this.selectedBodyImageFile = file;
    }
    this.contentService.uploadSession.filesMap[file.file.name] = { file };

    if (opts.convertible === false) {
      this.attachedPhotoName[which] = file.file.name;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (which === "serial") {
        this.displaySerialImage = reader.result;
      } else {
        this.displayBodyImage = reader.result;
      }
    };
    reader.onerror = (error) => {
      console.error("Error reading file", error);
    };
    reader.readAsDataURL(file._file);
  }

  /**
   * HEIC/HEIF (and any other exotic image type) to JPEG.
   *
   * Tries the browser's own decoder first via createImageBitmap — free, fast and
   * it succeeds on exactly the platform the files come from, since Safari on
   * iOS/macOS decodes HEIC natively. Only when that throws do we pull in
   * heic-to, which is lazily imported so its decoder stays out of the main bundle.
   */
  private async convertToJpeg(source: File): Promise<File> {
    const jpegName = source.name.replace(/\.[^.]+$/, "") + ".jpg";
    // Both decoders are tried, and the reason each one gave is kept. Without this a
    // failure is just "we couldn't read that photo", which says nothing about whether
    // the file was an undecodable variant, the wrong format, or the library not loading.
    const attempts: string[] = [];

    try {
      const bitmap = await createImageBitmap(source);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no 2d context");
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close?.();

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) throw new Error("canvas.toBlob returned null");
      return new File([blob], jpegName, { type: "image/jpeg" });
    } catch (error) {
      attempts.push(`browser decoder: ${this.describeError(error)}`);
    }

    try {
      const { heicTo } = await import("heic-to");
      const blob = await heicTo({ blob: source, type: "image/jpeg", quality: 0.9 });
      if (!blob || !blob.size) throw new Error("heic-to returned an empty result");
      return new File([blob], jpegName, { type: "image/jpeg" });
    } catch (error) {
      attempts.push(`heic-to: ${this.describeError(error)}`);
    }

    throw new Error(`Could not decode "${source.name}" (type="${source.type || "none"}", ${source.size} bytes) — ${attempts.join(" | ")}`);
  }

  /** Decoders reject with plain {code, message} objects, not Errors, so unwrap both. */
  private describeError(error: any): string {
    if (!error) return "unknown";
    if (typeof error === "string") return error;
    if (error.message) return `${error.name ? error.name + ": " : ""}${error.message}`;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  reEnterSerial() {
    this.activeModal.close();
    let modalRef = this.appService.showModal(RegisterProductComponent, { size: "lg" });
    modalRef.componentInstance.productKey = this.serial;
  }

  removeSerialImage() {
    this.serialImageUploader.clearQueue();
    this.displaySerialImage = undefined;
    this.selectedSerialImageFile = undefined;
    this.hoveringFileDrops.serial = false;
  }

  public selectedBodyImageFile: any;
  public displayBodyImage: any;
  setupBodyImageUploader() {
    this.bodyImageUploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    this.bodyImageUploader.onProgressAll = (progress: any) => {
      if (this.contentService.uploadSession.filesMap[this.selectedBodyImageFile.file.name]) {
        this.contentService.uploadSession.filesMap[this.selectedBodyImageFile.file.name].progress = progress;
      }
      this.fileUploadProgress.bodyImage = progress < 100 ? progress : null;
    };

    this.bodyImageUploader.onErrorItem = (error: any) => {
      alert("There was an error uploading the body image, please try again.");
      this.bodyImageUploader.clearQueue();
      this.fileUploadProgress.bodyImage = null;
      this.selectedBodyImageFile = undefined;
      this.displayBodyImage = undefined;
      this.hoveringFileDrops.body = false;
    };

    this.bodyImageUploader.onAfterAddingFile = (file) => {
      this.handleAddedImage(file, "body");
    };
  }

  removeBodyImage() {
    this.bodyImageUploader.clearQueue();
    this.displayBodyImage = undefined;
    this.selectedBodyImageFile = undefined;
    this.hoveringFileDrops.body = false;
  }

  /**
   * Fire Registration Started when SKU search fails (same as "I don't know the SKU" per audit).
   * Only runs when userService.appId === 'fender'.
   */
  private fireRegistrationStartedSkuSearchFailed() {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof te.registrationStarted !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
        const categoryMap: Record<string, string> = {
          electric_guitars: "instrument",
          acoustic_guitars: "instrument",
          bass_guitars: "instrument",
          amplifiers: "recording hardware",
          other: "instrument",
        };
        const avoCategory = categoryMap[this.category] || "instrument";
        const extra = {
          userId_: this.user?.["fc-id"] ?? undefined,
          prsId: this.user?.id ?? undefined,
          fcId: this.user?.["fc-id"] ?? undefined,
          category: avoCategory,
          action: "SKU / Model Number",
          errorName: "sku not found",
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.registrationStarted(payload);
      });
  }

  /**
   * Fire Registration Started when validation fails (missing photos or product name).
   * Only runs when userService.appId === 'fender'.
   */
  private fireRegistrationStartedValidationFailed(errorMessage: string) {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof te.registrationStarted !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
        const categoryMap: Record<string, string> = {
          electric_guitars: "instrument",
          acoustic_guitars: "instrument",
          bass_guitars: "instrument",
          amplifiers: "recording hardware",
          other: "instrument",
        };
        const avoCategory = categoryMap[this.category] || "instrument";
        const extra = {
          userId_: this.user?.["fc-id"] ?? undefined,
          prsId: this.user?.id ?? undefined,
          fcId: this.user?.["fc-id"] ?? undefined,
          category: avoCategory,
          action: "Submit",
          errorName: errorMessage,
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.registrationStarted(payload);
      });
  }

  /**
   * Fire Registration Started for successful fallback registration (before Registration Completed).
   * Only runs when userService.appId === 'fender'.
   */
  private fireRegistrationStartedFallback() {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof te.registrationStarted !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
        const categoryMap: Record<string, string> = {
          electric_guitars: "instrument",
          acoustic_guitars: "instrument",
          bass_guitars: "instrument",
          amplifiers: "recording hardware",
          other: "instrument",
        };
        const avoCategory = categoryMap[this.category] || "instrument";
        const extra = {
          userId_: this.user?.["fc-id"] ?? undefined,
          prsId: this.user?.id ?? undefined,
          fcId: this.user?.["fc-id"] ?? undefined,
          category: avoCategory,
          action: "Submit",
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.registrationStarted(payload);
      });
  }

  /**
   * Search the FMIC Products API by product name. `showInstrumentVariants` asks paeapi to return
   * guitars and basses one row per SKU so the customer can pick their actual colour/finish;
   * everything else (pedals, amps) still collapses to one row per product family, where the
   * individual SKUs are region/packaging noise they wouldn't recognize.
   */
  public searchProducts(appendPage: boolean = false) {
    const query = this.productQuery.trim();
    if (!query) return;
    if (appendPage) {
      this.loadingMoreProducts = true;
    } else {
      this.searchingProducts = true;
      this.productResults = [];
      this.productsNextToken = null;
      this.selectedProduct = undefined;
    }

    this.productService
      .searchFenderGear({
        title: query,
        showInstrumentVariants: true,
        pageSize: 50,
        nextToken: appendPage && this.productsNextToken ? this.productsNextToken : undefined,
      })
      .subscribe({
        next: (result: any) => {
          const rows = (result?.items || []).map((item: any) => this.toProductRow(item));
          this.productResults = appendPage ? [...this.productResults, ...rows] : rows;
          this.productsNextToken = result?.pagination?.nextToken || null;
          this.searchingProducts = false;
          this.loadingMoreProducts = false;
          this.productsSearched = true;
          this.skuNotFound = !this.productResults.length;
          if (!this.productResults.length) this.fireRegistrationStartedSkuSearchFailed();
        },
        error: () => {
          this.searchingProducts = false;
          this.loadingMoreProducts = false;
          this.productsSearched = true;
          this.productResults = [];
          this.skuNotFound = true;
          this.fireRegistrationStartedSkuSearchFailed();
        },
      });
  }

  public loadMoreProducts() {
    if (!this.productsNextToken || this.loadingMoreProducts) return;
    this.searchProducts(true);
  }

  private toProductRow(item: any): FenderProductSearchResult {
    const parts: string[] = [];
    if (item.color) parts.push(item.color);
    if (item.series) parts.push(item.series);
    if (item.bodyType) parts.push(item.bodyType);
    return {
      sku: item.sku,
      title: item.title,
      imageUrl: item.imageUrl,
      productNo: item.productNo,
      color: item.color || "",
      series: item.series || "",
      bodyType: item.bodyType || "",
      taxonomy: item.taxonomy || "",
      requiresPhotos: !!item.requiresPhotos,
      variantCount: item.variantCount || 1,
      subData: parts,
      subtitle: parts.join(" · "),
    };
  }

  public selectProduct(product: FenderProductSearchResult) {
    // PreSonus hardware SKUs (277*/278*/279*) register through the hardware flow, not as Fender
    // gear — same guard the old SKU input applied, moved to the point of selection.
    if (product.sku.startsWith("277") || product.sku.startsWith("278") || product.sku.startsWith("279")) {
      this.close();
      this.inelligibleProductEntered.emit("hardware");
      return;
    }
    this.selectedProduct = product;
    this.sku = product.sku;
    this.unknownSku = false;
    this.skuNotFound = false;
    this.photosRequired = product.requiresPhotos;
    this.category = fmicTaxonomyToGearCategory(product.taxonomy);
  }

  public clearSelectedProduct() {
    this.selectedProduct = undefined;
    this.sku = "";
    this.photosRequired = true;
  }

  /**
   * Show the manual entry form — the customer searched and couldn't find their gear, so they
   * describe it themselves and photos become mandatory.
   */
  public enterProductManually() {
    this.clearSelectedProduct();
    this.onUnknownSkuClick();
  }

  fileOverBase(e, type) {
    this.hoveringFileDrops[type] = e;
  }

  public registeredMissingSerial: any;
  submit() {
    // Transcoding a full-size HEIC takes several seconds. Without this the customer
    // hits Submit on a photo they have already chosen and is told it's missing,
    // because selectedSerialImageFile isn't assigned until the conversion resolves.
    if (this.pendingPhotoConversions.size) {
      this.saving = true;
      Promise.all(this.pendingPhotoConversions).then(() => {
        this.saving = false;
        this.submit();
      });
      return;
    }

    const productSelected = !!this.selectedProduct;

    // Photos are the verification of last resort: always on the manual path, and on
    // requiresPhotos families (Tone Master Pro) even though the customer told us the exact
    // product, because registering one grants free software. paeapi enforces the same rule.
    if (this.photosRequired && (!this.selectedSerialImageFile || !this.selectedBodyImageFile)) {
      const errorMsg = this.translate.instant("products.register.missing_fender_serial.validation_missing_photos");
      this.appService.alert(errorMsg);
      this.fireRegistrationStartedValidationFailed(errorMsg);
      return;
    }
    if (!productSelected && (!this.productName || this.productName.trim() === "")) {
      const errorMsg = this.translate.instant("products.register.missing_fender_serial.validation_missing_product_name");
      this.appService.alert(errorMsg);
      this.fireRegistrationStartedValidationFailed(errorMsg);
      return;
    }

    // Fire Registration Started for fallback registration (before submitting)
    // This ensures the event fires on successful fallback registration
    if (!productSelected && this.userService.appId === "fender") {
      this.fireRegistrationStartedFallback();
    }

    this.saving = true;
    this.submitArgs = {
      serial: this.userService.user.id + "#" + this.serial,
      productName: productSelected ? this.selectedProduct.title : this.productName,
      shopLink: this.shopLink,
      userId: this.user.id,
      category: this.category,
    };

    if (productSelected) {
      this.submitArgs.sku = this.selectedProduct.sku;
      // Sent alongside the sku so paeapi can tell whether this family requires photos without
      // re-querying PIM. It falls back to deriving the family from the sku, so this is a
      // convenience, not a trust boundary.
      this.submitArgs.productNo = this.selectedProduct.productNo;
    }

    // Upload whenever the customer attached photos — required on the manual and requiresPhotos
    // paths, and simply absent for an ordinary identified product.
    if (this.selectedSerialImageFile || this.selectedBodyImageFile) {
      this.uploadPhotosThenSave();
    } else {
      this.saveMissingSerial();
    }
  }

  private saveMissingSerial() {
    this.productService.saveMissingFenderSerial(this.submitArgs).subscribe({
      next: (result) => {
        this.productService.getProducts().subscribe(() => {
          this.saving = false;
          this.registeredMissingSerial = result;
          this.savingComplete = true;
          // Fire Registration Completed after successful fallback registration
          this.fireRegistrationCompleted();
        });
      },
      error: (error) => {
        this.saving = false;
        this.appService.alertError(error);
      },
    });
  }

  /**
   * Get presigned URLs sequentially, then upload the attached files in parallel
   * before saving. Either photo may be absent — a lone photo is still worth more
   * to support than none, so we upload whatever the customer attached.
   */
  private uploadPhotosThenSave() {
    const hasSerial = !!this.selectedSerialImageFile;
    const hasBody = !!this.selectedBodyImageFile;

    let uploadsCompleted = 0;
    const uploadsNeeded = (hasSerial ? 1 : 0) + (hasBody ? 1 : 0);

    const checkCompletion = () => {
      uploadsCompleted++;
      if (uploadsCompleted === uploadsNeeded) {
        // now save it to dynamo!
        this.saveMissingSerial();
      }
    };

    const doBody = () => {
      this.getPresignedURLForBodyImage(() => {
        this.startBodyImageUpload(checkCompletion);
      });
    };

    if (hasSerial) {
      // Get presigned URL for serial image first
      this.getPresignedURLForSerialImage(() => {
        // Start serial image upload
        this.startSerialImageUpload(checkCompletion);

        // After serial URL request is done, get presigned URL for body image
        if (hasBody) this._win.setTimeout(doBody, 200);
      });
    } else if (hasBody) {
      doBody();
    }
  }

  viewGear() {
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Missing Fender Serial" });
            const extra: Record<string, any> = {
              ctaText: "View My Gear",
              ctaLocation: "Missing Fender Serial",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    this.appService.closeModals();
    let key = this.registeredMissingSerial.sku || "sn-" + this.registeredMissingSerial.serial.split("#")[1].toUpperCase();
    this._router.navigate(["/products/detail/", key]);
  }

  getPresignedURLForSerialImage(onComplete: () => void) {
    this.contentService
      .getPresignedUploadURL({
        folder: `missing-fender-serials/${this.user.id}/${this.serial}/serial-image`,
        fileName: this.selectedSerialImageFile.file.name,
        type: "generic_file",
        mimetype: this.selectedSerialImageFile.file.type,
        extension: this.fileExtension(this.selectedSerialImageFile.file.name),
      })
      .subscribe((result) => {
        if (result.file) {
          this.submitArgs.serialImageUrl = result.file.url;
          this.contentService.uploadSession.filesMap[result.file.fileName].paeEntity = result.file;
          this.serialImageUploader.queue.forEach((file) => {
            let uploadHeaders = [];
            // Must be byte-identical to whatever paeapi signed, or S3 rejects the PUT with
            // SignatureDoesNotMatch. An older paeapi returns no contentType and signs without
            // one, so falling back to undefined preserves the previous (unsigned) behaviour.
            uploadHeaders.push({ name: "Content-Type", value: result.contentType });
            uploadHeaders.push({ name: "Content-Disposition", value: encodeFilenameForHeader(this.selectedSerialImageFile.file.name) });
            file.headers = uploadHeaders;
            file.url = result.url;
          });
          onComplete();
        } else {
          this.fileUploadProgress.serialImage = null;
          this.removeImage("serialImage");
          alert("There was an error getting upload URL for the serial image, please try again.");
          onComplete();
        }
      });
  }

  startSerialImageUpload(onComplete: () => void) {
    this.serialImageUploader.queue.forEach((file) => {
      file.onComplete = () => onComplete();
      this.serialImageUploader.uploadItem(file);
    });
  }

  getPresignedURLForBodyImage(onComplete: () => void) {
    this.contentService
      .getPresignedUploadURL({
        folder: `missing-fender-serials/${this.user.id}/${this.serial}/body-image`,
        fileName: this.selectedBodyImageFile.file.name,
        type: "generic_file",
        mimetype: this.selectedBodyImageFile.file.type,
        extension: this.fileExtension(this.selectedBodyImageFile.file.name),
      })
      .subscribe((result) => {
        if (result.file) {
          this.submitArgs.bodyImageUrl = result.file.url;
          this.contentService.uploadSession.filesMap[result.file.fileName].paeEntity = result.file;
          this.bodyImageUploader.queue.forEach((file) => {
            let uploadHeaders = [];
            // See the note in getPresignedURLForSerialImage — this must match the signed value.
            uploadHeaders.push({ name: "Content-Type", value: result.contentType });
            uploadHeaders.push({ name: "Content-Disposition", value: encodeFilenameForHeader(this.selectedBodyImageFile.file.name) });
            file.headers = uploadHeaders;
            file.url = result.url;
          });
          onComplete();
        } else {
          this.fileUploadProgress.bodyImage = null;
          this.removeImage("bodyImage");
          alert("There was an error getting upload URL for the body image, please try again.");
          onComplete();
        }
      });
  }

  startBodyImageUpload(onComplete: () => void) {
    this.bodyImageUploader.queue.forEach((file) => {
      file.onComplete = () => onComplete();
      this.bodyImageUploader.uploadItem(file);
    });
  }

  removeImage(type) {
    if (type == "serialImage") {
      this.displaySerialImage = null;
      this.selectedSerialImageFile = null;
      this.attachedPhotoName.serial = undefined;
      this.serialImageUploader.clearQueue();
      // delete args.serialImageUrl;
    } else if (type == "bodyImage") {
      this.displayBodyImage = null;
      this.selectedBodyImageFile = null;
      this.attachedPhotoName.body = undefined;
      this.bodyImageUploader.clearQueue();
      // delete args.bodyImageUrl;
    }
  }

  close() {
    this.activeModal.close();
  }
}
